// Profanity filter utility for family-friendly events
// Handles common bypass techniques: leetspeak, spacing, repeated chars

// Expanded word list for family-friendly contexts (church youth dances, etc.)
const PROFANITY_LIST = [
  'damn', 'hell', 'crap', 'shit', 'fuck', 'ass', 'bitch', 'bastard',
  'dick', 'cock', 'pussy', 'slut', 'whore', 'piss', 'tits',
  'nigger', 'nigga', 'faggot', 'fag', 'retard', 'retarded',
  'cunt', 'twat', 'wanker', 'bollocks',
  'asshole', 'arsehole', 'bullshit', 'horseshit', 'dipshit', 'shithead',
  'motherfucker', 'fucker', 'fuckhead', 'fuckface', 'fucktard',
  'dumbass', 'jackass', 'badass', 'fatass', 'smartass', 'hardass',
  'goddamn', 'goddammit',
  'blowjob', 'handjob',
  'boobs', 'titties',
  'penis', 'vagina',
  'stfu', 'gtfo', 'lmfao',
];

// Map of common leetspeak substitutions
const LEET_MAP: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's',
  '7': 't', '8': 'b', '@': 'a', '$': 's', '!': 'i',
  '+': 't', '(': 'c', '|': 'l',
};

/**
 * Normalize text by converting leetspeak and removing separator tricks
 */
function normalizeLeet(text: string): string {
  let normalized = '';
  for (const char of text.toLowerCase()) {
    normalized += LEET_MAP[char] || char;
  }
  return normalized;
}

/**
 * Remove common separator characters used to bypass filters
 * e.g., "f.u.c.k", "f u c k", "f-u-c-k", "f_u_c_k"
 */
function removeSeparators(text: string): string {
  return text.replace(/[\s.\-_*+~`'"^,;:!?]/g, '');
}

/**
 * Collapse repeated characters: "fuuuuck" -> "fuck"
 */
function collapseRepeats(text: string): string {
  return text.replace(/(.)\1{2,}/g, '$1');
}

/**
 * Filter profanity from a string, replacing bad words with asterisks.
 * Handles leetspeak, spacing tricks, and repeated characters.
 */
export function filterProfanity(text: string): string {
  if (!text) return text;

  let filtered = text;

  // Pass 1: Standard word-boundary matching (catches normal usage)
  for (const word of PROFANITY_LIST) {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    filtered = filtered.replace(regex, '*'.repeat(word.length));
  }

  // Pass 2: Check the normalized version for bypass attempts
  // We normalize a copy to detect, then replace in the original
  const normalizedFull = collapseRepeats(removeSeparators(normalizeLeet(filtered)));

  for (const word of PROFANITY_LIST) {
    if (normalizedFull.includes(word)) {
      // Find and replace the obfuscated version in the filtered text
      // Build a regex that allows separators and leetspeak between each character
      const flexPattern = word
        .split('')
        .map(ch => {
          // Build character class that matches the char and its leet variants
          const leetChars = Object.entries(LEET_MAP)
            .filter(([, v]) => v === ch)
            .map(([k]) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
          const charClass = leetChars.length > 0
            ? `[${ch}${leetChars.join('')}]`
            : ch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          return `${charClass}+`;
        })
        .join('[\\s.\\-_*+~`\'"^,;:!?]*');

      const flexRegex = new RegExp(`\\b${flexPattern}\\b`, 'gi');
      filtered = filtered.replace(flexRegex, (match) => '*'.repeat(match.length));
    }
  }

  return filtered;
}

/**
 * Check if text contains profanity (including bypass attempts)
 */
export function containsProfanity(text: string): boolean {
  if (!text) return false;

  const lowerText = text.toLowerCase();

  // Check standard word boundaries
  for (const word of PROFANITY_LIST) {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    if (regex.test(lowerText)) return true;
  }

  // Check normalized version for bypass attempts (word-bounded to avoid substring false positives)
  const normalized = collapseRepeats(removeSeparators(normalizeLeet(lowerText)));
  return PROFANITY_LIST.some(word => new RegExp(`\\b${word}\\b`).test(normalized));
}
